/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */
import { useState, useEffect } from "react";

// Extended breakpoint system (compatible with use-viewport.jsx)
export const BREAKPOINTS = {
    MOBILE_SMALL: "320px",      // 320 - 575px
    MOBILE_LARGE: "576px",      // 576 - 767px  
    TABLET: "768px",            // 768 - 991px
    DESKTOP_SMALL: "992px",     // 992 - 1200px
    DESKTOP_MEDIUM: "1201px",   // 1201 - 1440px
    DESKTOP_LARGE: "1920px",    // 1920px
    DESKTOP_XL: "2560px"        // 2560px
};

// Extended viewport size enums (compatible with use-viewport.jsx)
export const VIEWPORT_SIZES = {
    MOBILE_SMALL: "MOBILE_SMALL",
    MOBILE_LARGE: "MOBILE_LARGE",
    TABLET: "TABLET",
    DESKTOP_SMALL: "DESKTOP_SMALL",
    DESKTOP_MEDIUM: "DESKTOP_MEDIUM",
    DESKTOP_LARGE: "DESKTOP_LARGE",
    DESKTOP_XL: "DESKTOP_XL"
};

export const VIEWPORT_SIZES_MOBILE = [VIEWPORT_SIZES.MOBILE_SMALL, VIEWPORT_SIZES.MOBILE_LARGE, VIEWPORT_SIZES.TABLET, VIEWPORT_SIZES.DESKTOP_SMALL];
export const VIEWPORT_SIZES_DESKTOP = [VIEWPORT_SIZES.DESKTOP_XL, VIEWPORT_SIZES.DESKTOP_LARGE, VIEWPORT_SIZES.DESKTOP_MEDIUM];

// Function to determine viewport size based on screen width (compatible with use-viewport.jsx)
export const getViewportSize = (width) => {
    if (width >= parseInt(BREAKPOINTS.DESKTOP_XL)) {
        return VIEWPORT_SIZES.DESKTOP_XL;
    }
    if (width >= parseInt(BREAKPOINTS.DESKTOP_LARGE)) {
        return VIEWPORT_SIZES.DESKTOP_LARGE;
    }
    if (width >= parseInt(BREAKPOINTS.DESKTOP_MEDIUM)) {
        return VIEWPORT_SIZES.DESKTOP_MEDIUM;
    }
    if (width >= parseInt(BREAKPOINTS.DESKTOP_SMALL)) {
        return VIEWPORT_SIZES.DESKTOP_SMALL;
    }
    if (width >= parseInt(BREAKPOINTS.TABLET)) {
        return VIEWPORT_SIZES.TABLET;
    }
    if (width >= parseInt(BREAKPOINTS.MOBILE_LARGE)) {
        return VIEWPORT_SIZES.MOBILE_LARGE;
    }
    return VIEWPORT_SIZES.MOBILE_SMALL;
};

const useWindowSize = () => {
    // Legacy SIZES enum (maintained for backward compatibility)
    const SIZES = {
        SMALL: "small",
        MEDIUM: "medium",
        LARGE: "large",
    };
    // Legacy breakpoints (maintained for backward compatibility)
    const limit_sm = 1061;
    const limit_md = 1199;
    const min_lg = 1200;

    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    // Legacy state variables (maintained for backward compatibility)
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= min_lg);
    const [isTablet, setIsTablet] = useState(window.innerWidth <= limit_md && window.innerWidth >= limit_sm);
    const [isPhone, setIsPhone] = useState(window.innerWidth < limit_sm);
    const [currentSize, setCurrentSize] = useState(SIZES.LARGE);
    
    // New viewport state variables (compatible with use-viewport.jsx)
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [viewportSize, setViewportSize] = useState(() => getViewportSize(window.innerWidth));

    const determineSize = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        // Update window size
        setWindowSize({ width, height });
        
        // Update legacy state variables
        const isLarge = width >= min_lg;
        setIsDesktop(isLarge);
        const isMedium = width <= limit_md && width >= limit_sm;
        setIsTablet(isMedium);
        const isSmall = width < limit_sm;
        setIsPhone(isSmall);
        
        if (isLarge) {
            setCurrentSize(SIZES.LARGE);
        } else if (isMedium) {
            setCurrentSize(SIZES.MEDIUM);
        } else if (isSmall) {
            setCurrentSize(SIZES.SMALL);
        } else {
            setCurrentSize(SIZES.LARGE);
        }
        
        // Update new viewport state variables
        setScreenWidth(width);
        setViewportSize(getViewportSize(width));
    };
    useEffect(() => {
        determineSize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleResize = () => {
            determineSize();
        };

        window.addEventListener("resize", handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        // Legacy API (maintained for backward compatibility)
        windowSize,
        width: windowSize.width,
        height: windowSize.height,
        isDesktop,
        isTablet,
        isPhone,
        SIZES,
        currentSize,
        
        // New viewport API (compatible with use-viewport.jsx)
        screenWidth,
        viewportSize,
        
        // Additional utility exports
        getViewportSize,
        BREAKPOINTS,
        VIEWPORT_SIZES,
        VIEWPORT_SIZES_MOBILE,
        VIEWPORT_SIZES_DESKTOP
    };
};

// Compatibility alias for use-viewport.jsx
export const useViewport = () => {
    const { screenWidth, viewportSize } = useWindowSize();
    return { screenWidth, viewportSize };
};

export { useWindowSize };
